import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { getExpectedCNAME, type HostnamePointerSite } from '@gitbook/app-database';
import { ModalStepperLayout, useStepper } from '@gitbook/react-uikit';
import { Dialog, useDialogClose } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiClient, useSiteContext } from '../../state';
import { SiteConfigureSubdomainStep } from './ConfigureSubdomainStep';
import { SiteGoLiveStep } from './GoLiveStep';
import { SiteSelectSubdomainStep } from './SelectSubdomainStep';

export function SiteCustomHostnameDialog() {
    const closeDialog = useDialogClose();
    return (
        <Dialog size={{ width: 724 }}>
            <SiteCustomHostnameDialogContent onClose={closeDialog} />
        </Dialog>
    );
}

function SiteCustomHostnameDialogContent(props: { onClose: () => void }) {
    const { site, organizationId, analyticsContext } = useSiteContext();

    const cname = getExpectedCNAME({
        type: 'organization',
        organization: organizationId,
    });

    const [hostname, setHostname] = React.useState(site.hostname || '');

    const [processRestarted, setProcessRestarted] = React.useState(false);
    const [loading, wrapLoading] = useLoadingState(false);

    const pointer: HostnamePointerSite = {
        type: 'site',
        site: site.id,
    };

    useSetAnalyticsScreen(
        {
            ...analyticsContext,
            screen: 'site_custom_domain',
        },
        [analyticsContext]
    );

    /*
     * User selected a domain.
     */
    const onSelect = (newDomain: string) => {
        setHostname(newDomain);
        onStepForward();
    };

    /*
     * User configured the hostname.
     */
    const onConfigure = () => {
        onStepForward();

        analytics.logEvent(
            analytics.createSiteEvent(
                { id: site.id, organization: organizationId },
                {
                    type: 'site_edit_custom_domain',
                    customDomain: hostname,
                }
            )
        );
    };

    /*
     * Domain is live so we can show the last step as completed
     */
    const onLive = () => {
        onStepForward();
    };

    /*
     * User want's to revalidate DNS for the custom hostname
     */
    const onRetry = async () => {
        await wrapLoading(async () => {
            await apiClient.customHostnames.dnsRevalidateCustomHostname(hostname);
        });
    };

    const onRestart = () => {
        setProcessRestarted(true);
        onStepperRestart();
    };

    const steps = [
        {
            title: 'Select a subdomain',
            content: (
                <SiteSelectSubdomainStep
                    pointer={pointer}
                    initialValue={hostname}
                    onSelect={onSelect}
                    analyticsContext={analyticsContext}
                />
            ),
        },
        {
            title: 'Configure your DNS',
            content: (
                <SiteConfigureSubdomainStep
                    cname={cname}
                    pointer={pointer}
                    hostname={hostname}
                    processRestarted={processRestarted}
                    onConfigure={onConfigure}
                    analyticsContext={analyticsContext}
                />
            ),
        },
        {
            title: 'Check your domain',
            content: (
                <SiteGoLiveStep
                    cname={cname}
                    pointer={pointer}
                    hostname={hostname}
                    loading={loading}
                    onRestart={onRestart}
                    onLive={onLive}
                    onRetry={onRetry}
                    onFinish={props.onClose}
                    analyticsContext={analyticsContext}
                />
            ),
        },
    ];

    const {
        completedSteps,
        currentStepIndex,
        onStepForward,
        onStepBack,
        onRestart: onStepperRestart,
    } = useStepper({
        stepsCount: steps.length,
        initialStep: site.hostname ? 1 : 0,
    });

    return (
        <ModalStepperLayout
            onStepBack={onStepBack}
            steps={steps}
            completedSteps={completedSteps}
            currentStepIndex={currentStepIndex}
        >
            {steps[currentStepIndex].content}
        </ModalStepperLayout>
    );
}
